import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Institutional Ecosystem Map
 *
 * Living organic visualization of the Celestial Seed institution.
 * A trunk (NEOS → Sanctuary) branches into 11 departments. Each node
 * is interactive: clicking reveals mission, initiatives, opportunities.
 * Departments with dedicated pages navigate; the rest open a preview
 * modal with placeholder institutional language.
 */

const DEPTS = [
  { key: "healing", t: "Healing", to: "/rebirthing", mission: "Rebirthing Genesis, breathwork, nervous-system regulation, retreat medicine, ceremonial integration.", opportunities: ["Attend a retreat", "Book a private session", "Join the Healer Fellowship"] },
  { key: "education", t: "Education", mission: "School of Practical Wisdom & Life — seasonal intensives, apprenticeships, guilds, certifications, the Knowledge Vault.", opportunities: ["Enroll in a course", "Apply to teach", "Join a guild"] },
  { key: "ecology", t: "Ecology", mission: "Land stewardship, food forest, watershed restoration, regenerative agriculture, wildlife corridors.", opportunities: ["Volunteer on the land", "Sponsor a food forest acre", "Contribute research"] },
  { key: "culture", t: "Culture & Arts", to: "/culture", badge: "Central", mission: "Artist residencies, music, dance, film, indigenous knowledge, traditional craft, public festivals — culture as regenerative force.", opportunities: ["Propose a residency", "Perform at a festival", "Document the sanctuary"] },
  { key: "community", t: "Community", to: "/volunteer", mission: "Volunteer & Operator Corps, residents, stewards, partners — the human fabric of the sanctuary.", opportunities: ["Volunteer 3–6 months", "Apply to steward", "Bring a small group"] },
  { key: "development", t: "Development", to: "/dubbi", mission: "Cloud Forrest EcoVillage — bamboo tiny homes, master planning, natural building, land expansion.", opportunities: ["Reserve a home", "Invest in Cloud Forrest EcoVillage", "Design with us"] },
  { key: "media", t: "Media", mission: "Podcast, books, film, photography, newsletter — the storytelling arm of the institution.", opportunities: ["Contribute writing", "Photograph the land", "Produce a film"] },
  { key: "funding", t: "Funding", to: "/donate", mission: "Grant Center, sponsorships, monthly giving, healer scholarships, campaign tracking, impact reporting.", opportunities: ["Give monthly", "Sponsor a healer", "Fund a campaign"] },
  { key: "alliance", t: "Global Alliance", mission: "Tribes of Humanity — international network of partner sanctuaries, teachers, healers, artists, builders.", opportunities: ["Become a partner node", "Cross-pollinate residencies", "Host a delegation"] },
  { key: "research", t: "Research", mission: "Institute for Regenerative Civilization — long-form publications, policy, systems research, institutional development.", badge: "Coming", opportunities: ["Propose a study", "Contribute a paper", "Fund a fellowship"] },
  { key: "admin", t: "Administration", mission: "CRM, applications, calendar, financial dashboard, communications, executive oversight — the operational nervous system.", opportunities: ["Join operations", "Steward a workflow", "Systems consulting"] },
  { key: "archives", t: "Archives & Legacy", mission: "The institutional memory of Celestial Seed. Founding documents, oral histories, land history, decision archives — the bones that let the institution stand upright across generations.", opportunities: ["Contribute an oral history", "Archive a founding document", "Steward the record"] },
  { key: "observatory", t: "Observatory, Learning & Evaluation", mission: "The eyes, ears, and mirror of the institution. Measures impact, collects stories, evaluates programs, observes the land's regeneration, produces the Annual State of the Institution. An institution that does not observe itself cannot evolve with wisdom.", opportunities: ["Contribute a participant story", "Support annual reporting", "Fund an ecological baseline study"] },
];

// Thirteen nodes arranged organically (mycelial)
const POSITIONS = [
  { top: "6%",  left: "8%"  }, { top: "4%",  left: "32%" }, { top: "6%",  left: "56%" }, { top: "4%",  left: "82%" },
  { top: "30%", left: "16%" }, { top: "28%", left: "42%" }, { top: "30%", left: "68%" }, { top: "32%", left: "92%" },
  { top: "56%", left: "10%" }, { top: "54%", left: "36%" }, { top: "58%", left: "62%" }, { top: "52%", left: "88%" },
  { top: "82%", left: "48%" },
];

export default function EcosystemMap() {
  const [active, setActive] = useState(null);

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-5">
          <div className="overline mb-3">Institutional Ecosystem</div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]">A living forest,<br/>not a bureaucracy.</h2>
        </div>
        <p className="lg:col-span-7 self-end text-ink-soft leading-relaxed">
          NEOS is the nervous system. Celestial Seed Sanctuary is the trunk. The thirteen departments below are branches of a single living body — each with its own soil, its own weather, and its own season. Click any branch to enter.
        </p>
      </div>

      {/* The Map */}
      <div className="relative bg-surface border border-line rounded-3xl overflow-hidden">
        {/* Trunk header — NEOS → Sanctuary */}
        <div className="relative z-10 flex flex-col items-center pt-10 pb-4">
          <div className="rounded-full border border-forest/20 bg-forest text-bone px-6 py-2.5">
            <div className="overline text-bone/70 leading-none mb-1">Nervous System</div>
            <div className="font-serif text-lg leading-tight">NEOS</div>
          </div>
          <div className="w-px h-8 bg-forest/25" />
          <div className="rounded-full border border-terracotta/40 bg-bone px-8 py-3 shadow-[0_10px_28px_-16px_rgba(200,111,88,0.55)]">
            <div className="overline text-terracotta leading-none mb-1">The Trunk</div>
            <div className="font-serif text-xl leading-tight text-ink">Celestial Seed Sanctuary</div>
          </div>
          <div className="w-px h-6 bg-forest/20" />
        </div>

        {/* Branch canvas */}
        <div className="relative h-[720px] md:h-[640px] mx-auto max-w-5xl">
          {/* Mycelial connector SVG — organic curves from center to each node */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" aria-hidden="true">
            <defs>
              <linearGradient id="branch-grad" x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" stopColor="#2C3D30" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#2C3D30" stopOpacity="0.08" />
              </linearGradient>
            </defs>
            {POSITIONS.map((p, i) => {
              const cx = parseFloat(p.left) + 8; // approx node center
              const cy = parseFloat(p.top) + 6;
              const midY = (cy + 2) / 2;
              const stableKey = DEPTS[i]?.key || `pos-${p.top}-${p.left}`;
              return (
                <path key={stableKey} d={`M 50 2 C 50 ${midY}, ${cx} ${midY}, ${cx} ${cy}`}
                  stroke="url(#branch-grad)" strokeWidth="0.25" fill="none" />
              );
            })}
          </svg>

          {/* Nodes */}
          {DEPTS.map((d, i) => {
            const pos = POSITIONS[i];
            const isCentral = d.key === "culture";
            return (
              <button
                key={d.key}
                type="button"
                data-testid={`map-node-${d.key}`}
                onClick={() => setActive(d)}
                style={pos}
                className={`absolute -translate-x-1/2 -translate-y-1/2 group rounded-full px-4 py-2.5 border transition-all duration-300 whitespace-nowrap text-left
                  ${isCentral
                    ? "bg-terracotta text-bone border-terracotta shadow-[0_16px_40px_-16px_rgba(200,111,88,0.7)] hover:scale-[1.04]"
                    : "bg-bone text-ink border-line hover:border-forest hover:shadow-[0_12px_28px_-14px_rgba(44,61,48,0.35)] hover:-translate-y-1"}`}
              >
                <div className={`text-[10px] uppercase tracking-[0.18em] leading-none mb-1 ${isCentral ? "text-bone/80" : "text-ink-soft"}`}>Branch</div>
                <div className="font-serif text-base leading-tight flex items-center gap-2">
                  {d.t}
                  {d.badge && <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full ${isCentral ? "bg-bone/20 text-bone" : "bg-forest/10 text-forest"}`}>{d.badge}</span>}
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-center pb-8 overline">Every branch a doorway · Click to enter</div>
      </div>

      {/* Department Preview Modal */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-bone max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <div className="overline mb-2">Department</div>
                <DialogTitle className="font-serif text-3xl leading-tight">{active.t}</DialogTitle>
                <DialogDescription className="text-ink-soft mt-3 leading-relaxed">{active.mission}</DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <div className="overline mb-2">Ways to participate</div>
                <ul className="space-y-2">
                  {active.opportunities.map((o) => (
                    <li key={o} className="flex items-start gap-2 text-sm text-ink"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />{o}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 flex gap-3">
                {active.to ? (
                  <Link to={active.to} onClick={() => setActive(null)} data-testid={`map-enter-${active.key}`} className="pill pill-primary text-sm">Enter {active.t} →</Link>
                ) : (
                  <span className="pill pill-outline text-sm opacity-70 cursor-not-allowed">In development</span>
                )}
                <button onClick={() => setActive(null)} data-testid="map-close" className="pill pill-outline text-sm">Close</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
